import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Card, CardContent, Box, Typography, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, FormControl, Select, MenuItem,
  Chip, Avatar, IconButton, CircularProgress,
} from '@mui/material';
import {
  Add, School as SchoolIcon, CheckCircle, Cancel,
  Search as SearchIcon, OpenInNew as OpenInNewIcon,
  ArrowForward, Edit, ToggleOn, Delete, Send, Tune, Login as LoginIcon,
  InsertChartOutlined, ToggleOnOutlined, Storage as StorageIcon, CurrencyRupee,
} from '@mui/icons-material';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const ACTION_LABELS = {
  school_created:              'School created',
  school_updated:              'School updated',
  school_status_changed:       'Status changed',
  school_deleted:              'School deleted',
  school_delete_otp_requested: 'Delete OTP requested',
  feature_flag_updated:        'Feature flag toggled',
};

const ACTION_ICON_BG = {
  school_created:              '#10b98115',
  school_updated:              '#4F46E515',
  school_status_changed:       '#f59e0b15',
  school_deleted:              '#ef444415',
  school_delete_otp_requested: '#f59e0b15',
  feature_flag_updated:        '#6b728015',
};

const ACTION_ICON_COLOR = {
  school_created:              '#10b981',
  school_updated:              '#4F46E5',
  school_status_changed:       '#f59e0b',
  school_deleted:              '#ef4444',
  school_delete_otp_requested: '#f59e0b',
  feature_flag_updated:        '#6b7280',
};

const ACTION_ICON = {
  school_created:              <SchoolIcon sx={{ fontSize: 16 }} />,
  school_updated:              <Edit sx={{ fontSize: 16 }} />,
  school_status_changed:       <ToggleOn sx={{ fontSize: 16 }} />,
  school_deleted:              <Delete sx={{ fontSize: 16 }} />,
  school_delete_otp_requested: <Send sx={{ fontSize: 16 }} />,
  feature_flag_updated:        <Tune sx={{ fontSize: 16 }} />,
};

const HEALTH_BAND_COLOR = {
  critical:     { bg: '#fee2e2', text: '#991b1b', chip: 'error' },
  dormant:      { bg: '#f3f4f6', text: '#374151', chip: 'default' },
  at_risk:      { bg: '#fef3c7', text: '#92400e', chip: 'warning' },
  provisioning: { bg: '#e0e7ff', text: '#3730a3', chip: 'info' },
  healthy:      { bg: '#d1fae5', text: '#065f46', chip: 'success' },
};

const HEALTH_BAND_LABEL = {
  critical: 'Critical', dormant: 'Dormant', at_risk: 'At risk',
  provisioning: 'Provisioning', healthy: 'Healthy',
};

const planBgColor = (tier) => ({
  trial: '#fef3c7',
  basic: '#ede9fe',
  pro:   '#d1fae5',
}[tier] ?? '#f3f4f6');

const planTextColor = (tier) => ({
  trial: '#92400e',
  basic: '#5b21b6',
  pro:   '#065f46',
}[tier] ?? '#6b7280');

function groupByMonth(schools = []) {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.toLocaleDateString('en-IN', { month: 'short' }),
      year: d.getFullYear(),
      monthNum: d.getMonth(),
      count: 0,
    });
  }
  schools.forEach((s) => {
    const d = new Date(s.created_at);
    const entry = months.find((m) => m.year === d.getFullYear() && m.monthNum === d.getMonth());
    if (entry) entry.count += 1;
  });
  return months.map(({ month, count }) => ({ month, count }));
}

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / Math.pow(1024, exponent)).toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 2) return 'yesterday';
  return new Date(dateStr).toLocaleDateString('en-IN');
}

const safeGet = async (fn) => {
  try {
    return await fn();
  } catch {
    return null;
  }
};

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { startImpersonation, isImpersonating } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [allSchools, setAllSchools] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const handleImpersonate = async (e, school) => {
    e.stopPropagation();
    if (isImpersonating()) {
      toast.warning('You are already in an impersonation session. Exit it first.');
      return;
    }
    setImpersonatingId(school.id);
    const result = await startImpersonation(school.id);
    setImpersonatingId(null);

    if (result.success) {
      toast.success(`Viewing as ${school.name}`);
      navigate('/principal/dashboard');
    } else {
      toast.error(result.error);
    }
  };
  const [subscriptionStats, setSubscriptionStats] = useState({});
  const [storageTotals, setStorageTotals] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [healthWatchlist, setHealthWatchlist] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);

        const [dashRes, schoolsRes, auditRes, subsRes, storageRes, costsRes, healthRes] = await Promise.all([
          superAdminAPI.getDashboard(),
          superAdminAPI.getSchools(),
          safeGet(() => superAdminAPI.getAuditLog({ page: 1, page_size: 5 })),
          safeGet(() => superAdminAPI.getSubscriptions()),
          safeGet(() => superAdminAPI.getStorage()),
          safeGet(() => superAdminAPI.getApiCosts()),
          safeGet(() => superAdminAPI.getHealth()),
        ]);

        if (!alive) return;

        const subscriptions = subsRes?.data?.subscriptions ?? [];
        const subsBySchoolId = {};
        subscriptions.forEach((s) => { subsBySchoolId[s.school_id] = s; });

        const merged = (schoolsRes.data.schools ?? []).map((school) => ({
          ...school,
          plan_tier: subsBySchoolId[school.id]?.plan_tier,
          student_count: subsBySchoolId[school.id]?.student_count,
        }));

        const now = new Date();
        const expiring = subscriptions.filter((s) => {
          if (!s.current_period_end || s.status !== 'active') return false;
          const days = Math.ceil((new Date(s.current_period_end) - now) / 86400000);
          return days >= 0 && days <= 30;
        });

        setStats(dashRes.data.stats);
        setAllSchools(merged);
        setRecentEvents(auditRes ? auditRes.data.data.rows : []);
        setExpiringSoon(expiring);
        setSubscriptionStats(subsRes?.data?.stats ?? {});
        setStorageTotals(storageRes?.data?.storage?.totals ?? null);
        setCostSummary(costsRes?.data?.summary ?? null);
        const atRisk = (healthRes?.data?.schools ?? []).filter(
          (s) => s.band === 'critical' || s.band === 'dormant' || s.band === 'at_risk'
        );
        setHealthWatchlist(atRisk.slice(0, 5));
      } catch (error) {
        if (alive) {
          toast.error('Failed to load dashboard data');
          console.error(error);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredSchools = allSchools.filter((s) => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const schoolGrowthData = groupByMonth(allSchools);

  const subscriptionMixData = [
    { name: 'Trial',   value: parseInt(subscriptionStats.trial_count)   || 0, color: '#f59e0b' },
    { name: 'Basic',   value: parseInt(subscriptionStats.basic_count)   || 0, color: '#6366f1' },
    { name: 'Pro',     value: parseInt(subscriptionStats.pro_count)     || 0, color: '#10b981' },
    { name: 'Expired', value: parseInt(subscriptionStats.expired_count) || 0, color: '#ef4444' },
  ].filter((d) => d.value > 0);

  const kpis = [
    { label: 'Total Schools',  value: stats?.totalSchools    ?? 0, icon: <SchoolIcon />,  color: '#4F46E5' },
    { label: 'Active',         value: stats?.activeSchools   ?? 0, icon: <CheckCircle />, color: '#10b981' },
    { label: 'Inactive',       value: stats?.inactiveSchools ?? 0, icon: <Cancel />,      color: '#ef4444' },
    {
      label: 'Storage Used',
      value: formatBytes(storageTotals?.totalBytes),
      icon: <StorageIcon />,
      color: '#f59e0b',
      isText: true,
    },
    {
      label: 'Est. Cost (This Month)',
      value: `₹${Number(costSummary?.combined?.totalCostThisMonthInr ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
      icon: <CurrencyRupee />,
      color: '#6366f1',
      isText: true,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Super Admin Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<InsertChartOutlined />}
            onClick={() => navigate('/superadmin/analytics')}
          >
            Analytics
          </Button>
          <Button
            variant="outlined"
            startIcon={<ToggleOnOutlined />}
            onClick={() => navigate('/superadmin/features')}
          >
            Features & Storage
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/superadmin/schools/new')}
            sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#3730A3' } }}
          >
            Add School
          </Button>
        </Box>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} mb={3}>
        {kpis.map(({ label, value, icon, color, isText }) => (
          <Grid item xs={6} sm={4} md={2.4} key={label}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ bgcolor: `${color}15`, color, borderRadius: 1.5, p: 0.75, display: 'flex' }}>
                    {React.cloneElement(icon, { sx: { fontSize: 20 } })}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} lineHeight={1}>
                      {isText ? value : value.toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts row */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Schools joined (last 6 months)</Typography>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={schoolGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#4F46E5"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#4F46E5' }}
                  name="Schools"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Subscription mix</Typography>
            {subscriptionMixData.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" height={160}>
                <Typography variant="body2" color="text.secondary">No subscription data</Typography>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie
                      data={subscriptionMixData}
                      cx="50%" cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      dataKey="value"
                      paddingAngle={subscriptionMixData.length > 1 ? 3 : 0}
                    >
                      {subscriptionMixData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [v, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <Box flex={1}>
                  {subscriptionMixData.map(({ name, value, color }) => (
                    <Box key={name} display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" flex={1}>{name}</Typography>
                      <Typography variant="body2" fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Schools table */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2.5} pb={1.5} flexWrap="wrap" gap={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>Schools</Typography>
          <Box display="flex" gap={1.5} alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} /> }}
              sx={{ width: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} displayEmpty>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
              <TableCell>School</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="right">Students</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSchools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" py={3}>No schools found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSchools.slice(0, 8).map((school) => (
                <TableRow
                  key={school.id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  onClick={() => navigate(`/superadmin/schools/${school.id}`)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: 13, fontWeight: 700 }}>
                        {school.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{school.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{school.city || '—'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: school.status === 'Active' ? '#10b981' : '#9ca3af' }} />
                      <Typography variant="caption">{school.status}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={school.plan_tier ?? 'trial'}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20, bgcolor: planBgColor(school.plan_tier), color: planTextColor(school.plan_tier) }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{(parseInt(school.student_count) || 0).toLocaleString('en-IN')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title={school.status === 'Inactive' ? 'School is inactive' : 'View as this school'}
                      disabled={impersonatingId === school.id || school.status === 'Inactive'}
                      onClick={(e) => handleImpersonate(e, school)}
                      sx={{ color: '#7C3AED' }}
                    >
                      <LoginIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/superadmin/schools/${school.id}`); }}>
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Box px={2.5} py={1.5} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            size="small"
            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
            onClick={() => navigate('/superadmin/schools')}
            sx={{ color: '#4F46E5', fontSize: '0.8rem' }}
          >
            View all {allSchools.length} schools
          </Button>
        </Box>
      </Card>

      {/* Bottom row */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2.5} pb={1}>
              <Typography variant="subtitle1" fontWeight={600}>Recent activity</Typography>
              <Button size="small" onClick={() => navigate('/superadmin/audit-log')} sx={{ color: '#4F46E5', fontSize: '0.75rem' }}>
                View all
              </Button>
            </Box>
            {recentEvents.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">No recent activity</Typography>
              </Box>
            ) : (
              recentEvents.map((ev) => (
                <Box
                  key={ev.id}
                  display="flex"
                  alignItems="flex-start"
                  gap={1.5}
                  px={2.5}
                  py={1.25}
                  sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                >
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    bgcolor: ACTION_ICON_BG[ev.action] ?? '#6b728015',
                    color: ACTION_ICON_COLOR[ev.action] ?? '#6b7280',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {ACTION_ICON[ev.action] ?? <Tune sx={{ fontSize: 16 }} />}
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {ACTION_LABELS[ev.action] ?? ev.action}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {ev.school_name ?? ev.old_values?.name ?? 'Platform'} · {timeAgo(ev.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2.5} pb={1}>
              <Typography variant="subtitle1" fontWeight={600}>Needs attention</Typography>
              <Button size="small" onClick={() => navigate('/superadmin/schools')} sx={{ color: '#4F46E5', fontSize: '0.75rem' }}>
                View all
              </Button>
            </Box>

            {expiringSoon.length === 0 && healthWatchlist.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">All clear — nothing needs attention</Typography>
              </Box>
            ) : (
              <>
                {healthWatchlist.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 2.5, pt: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      At-risk schools
                    </Typography>
                    {healthWatchlist.map((s) => {
                      const bandStyle = HEALTH_BAND_COLOR[s.band] ?? HEALTH_BAND_COLOR.at_risk;
                      return (
                        <Box
                          key={s.school_id}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          px={2.5}
                          py={1.25}
                          sx={{ borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => navigate(`/superadmin/schools/${s.school_id}`)}
                        >
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500} noWrap>{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {s.reasons?.[0] ?? `Score ${s.score}`}
                            </Typography>
                          </Box>
                          <Chip
                            label={HEALTH_BAND_LABEL[s.band] ?? s.band}
                            size="small"
                            sx={{ fontSize: '0.7rem', bgcolor: bandStyle.bg, color: bandStyle.text }}
                          />
                        </Box>
                      );
                    })}
                  </>
                )}

                {expiringSoon.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 2.5, pt: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Expiring subscriptions
                    </Typography>
                    {expiringSoon.map((sub) => {
                      const days = Math.ceil((new Date(sub.current_period_end) - new Date()) / 86400000);
                      return (
                        <Box
                          key={sub.school_id}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          px={2.5}
                          py={1.25}
                          sx={{ borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => navigate(`/superadmin/schools/${sub.school_id}`)}
                        >
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500} noWrap>{sub.school_name}</Typography>
                            <Chip
                              label={sub.plan_tier}
                              size="small"
                              sx={{ fontSize: '0.65rem', height: 16, mt: 0.5, bgcolor: planBgColor(sub.plan_tier), color: planTextColor(sub.plan_tier) }}
                            />
                          </Box>
                          <Chip
                            label={days <= 7 ? `${days}d left` : `${days}d`}
                            size="small"
                            color={days <= 7 ? 'error' : 'warning'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SuperAdminDashboard;
